/* eslint-disable no-shadow */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import { ChevronDown, Forward, Check, FileText } from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import TimestampChip from '../../../common/TimestampChip';
import youtubeImage from './assets/youtube.avif';

interface MockChapter {
  id: string;
  title: string;
  notes: string;
  start: string;
  end?: string;
}

const mockChapters: MockChapter[] = [
  {
    id: '1',
    title: 'Project Introduction & Goals',
    notes:
      'Julian introduces the new React project and outlines key objectives including improved user experience, performance optimizations, and scalable architecture.',
    start: '00:35',
    end: '03:42',
  },
  {
    id: '2',
    title: 'Technical Architecture Discussion',
    notes:
      'Sarah presents the proposed technical stack and explains the microservices approach. The team discusses database choices and API design patterns.',
    start: '03:42',
    end: '07:18',
  },
  {
    id: '3',
    title: 'Timeline & Resource Planning',
    notes:
      'Discussion of project milestones, sprint planning, and resource allocation. Mike raises concerns about the Q4 deadline and suggests alternative approaches.',
    start: '07:18',
    end: '10:45',
  },
  {
    id: '4',
    title: 'Security & Compliance Requirements',
    notes:
      'Emma outlines security protocols, data protection requirements, and compliance standards. Discussion covers authentication, authorization, and GDPR considerations for the new platform.',
    start: '10:45',
    end: '14:22',
  },
  {
    id: '5',
    title: 'Testing Strategy & QA Process',
    notes:
      'Alex presents the comprehensive testing approach including unit tests, integration tests, and end-to-end testing. The team discusses CI/CD pipeline setup and automated testing workflows.',
    start: '14:22',
    end: '18:05',
  },
];

const formatTimestamp = (timestamp: string) => {
  const parts = timestamp.split(':');

  if (parts.length === 2) {
    const minutes = parseInt(parts[0], 10);
    if (minutes < 60) {
      return `00:${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours
      .toString()
      .padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}:${
      parts[1]
    }`;
  }
  if (parts.length === 3) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(
      2,
      '0'
    )}:${parts[2].padStart(2, '0')}`;
  }

  return timestamp;
};

const highlightText = (text: string, searchTerm: string) => {
  if (!searchTerm) return text;
  const regex = new RegExp(`(${searchTerm})`, 'gi');
  return text.split(regex).map((part, i) =>
    regex.test(part) ? (
      <Box
        key={i}
        component="mark"
        sx={{
          backgroundColor: 'warning.light',
          color: 'warning.contrastText',
          borderRadius: '4px',
          textDecoration: 'none',
        }}
      >
        {part}
      </Box>
    ) : (
      part
    )
  );
};

const ChapterAccordionDemo: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [expandedChapter, setExpandedChapter] = useState<string | null>(null);
  const [playingChapter, setPlayingChapter] = useState<string | null>(null);
  const [copiedChapter, setCopiedChapter] = useState<string | null>(null);
  const [demoStep, setDemoStep] = useState(1);
  const [showTimestamps] = useState(false); // Start with hidden timestamps
  const [showScreenshots] = useState(true);
  const [searchTerm] = useState('');

  // Demo animation sequence
  useEffect(() => {
    const timer = setTimeout(
      () => {
        switch (demoStep) {
          case 0:
            // Initial state - start immediately
            setDemoStep(1);
            break;
          case 1:
            // Expand first chapter
            setExpandedChapter('1');
            setDemoStep(2);
            break;
          case 2:
            // Show playing state
            setPlayingChapter('1');
            setDemoStep(3);
            break;
          case 3:
            // Collapse and expand second chapter
            setExpandedChapter('2');
            setPlayingChapter('2');
            setDemoStep(4);
            break;
          case 4:
            // Show share functionality
            setCopiedChapter('2');
            setDemoStep(5);
            break;
          case 5:
            // Reset copied state
            setCopiedChapter(null);
            setDemoStep(6);
            break;
          case 6:
            // Move to third chapter
            setExpandedChapter('3');
            setPlayingChapter('3');
            setDemoStep(7);
            break;
          case 7:
            // Reset and loop
            setExpandedChapter(null);
            setPlayingChapter(null);
            setDemoStep(0);
            break;
          default:
            break;
        }
      },
      demoStep === 0 ? 100 : 1500
    ); // Start immediately instead of 2000ms

    return () => clearTimeout(timer);
  }, [demoStep]);

  const handleChapterExpand = (chapterId: string) => {
    setExpandedChapter(expandedChapter === chapterId ? null : chapterId);
  };

  const handleTimestampClick = (chapterId: string) => {
    setPlayingChapter(chapterId);
  };

  const handleShare = async (chapterId: string) => {
    setCopiedChapter(chapterId);
    setTimeout(() => setCopiedChapter(null), 2000);
  };

  return (
    <Box
      sx={{
        p: 3,
        minHeight: 400,
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <FileText sx={{ fontSize: 20 }} />
          {t('library.tour.chapters.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('library.tour.chapters.description')}
        </Typography>
      </Box>

      {/* Chapter Accordions - exact styling from real component */}
      <Box sx={{ mx: -1 }}>
        <Stack spacing={0}>
          {mockChapters.map((chapter, index) => {
            const isExpanded = expandedChapter === chapter.id;
            const isPlaying = playingChapter === chapter.id;
            const isCopied = copiedChapter === chapter.id;

            return (
              <Accordion
                key={chapter.id}
                expanded={isExpanded}
                onChange={() => handleChapterExpand(chapter.id)}
                disableGutters
                sx={{
                  '&:before': {
                    display: 'none',
                  },
                  boxShadow: 'none',
                  borderRadius: 0,
                  borderTop: index === 0 ? 'none' : '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'transparent',
                  '&:first-of-type': {
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                  },
                  '&.Mui-expanded': {
                    margin: 0,
                  },
                  // eslint-disable-next-line @typescript-eslint/no-shadow
                  transition: (t) =>
                    t.transitions.create(['margin', 'background-color'], {
                      duration: t.transitions.duration.shortest,
                    }),
                  '& .MuiAccordionSummary-root': {
                    backgroundColor: isPlaying
                      ? 'secondary.light'
                      : 'background.paper',
                    '& .MuiTypography-root': {
                      color: 'text.primary',
                    },
                    '&.Mui-expanded .MuiTypography-root': {
                      fontWeight: 600,
                    },
                    '& .timestamp-hidden': {
                      opacity: 0,
                      transition: 'opacity 0.2s',
                    },
                    '&:hover .timestamp-hidden': {
                      opacity: 1,
                    },
                  },
                  '&.Mui-expanded .MuiAccordionSummary-root .timestamp-hidden': {
                    opacity: 1,
                  },
                  '&:hover .MuiAccordionSummary-root': {
                    backgroundColor: isPlaying
                      ? theme.palette.secondary.light
                      : 'action.hover',
                  },
                  '&:hover .share-button': {
                    opacity: 1,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ChevronDown />}
                  sx={{
                    minHeight: 'unset',
                    // eslint-disable-next-line @typescript-eslint/no-shadow
                    padding: (t) => t.spacing(0.75, 1.5),
                    '& .MuiAccordionSummary-content': {
                      margin: '4px 0',
                      '&.Mui-expanded': {
                        margin: '4px 0',
                      },
                    },
                    '&:hover .share-button': {
                      opacity: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flex: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 500,
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {highlightText(chapter.title, searchTerm)}
                      </Typography>
                      {/* Container for share button and timestamp */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          ml: 'auto',
                        }}
                      >
                        {/* Share button */}
                        {isExpanded && (
                          <Tooltip
                            title={t('library.tour.chapters.share.tooltip')}
                            placement="bottom"
                            arrow
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShare(chapter.id);
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              className="share-button"
                              sx={{
                                color: isCopied
                                  ? 'success.main'
                                  : 'primary.main',
                                padding: '4px',
                                opacity: 0,
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                              }}
                            >
                              {isCopied ? (
                                <Check fontSize="small" />
                              ) : (
                                <Forward fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* Timestamp chip */}
                        <TimestampChip
                          className={
                            showTimestamps ? undefined : 'timestamp-hidden'
                          }
                          timestamp={formatTimestamp(chapter.start).replace(
                            /^00:/,
                            ''
                          )}
                          onClick={() => handleTimestampClick(chapter.id)}
                        />
                      </Box>
                    </Box>
                  </Box>
                </AccordionSummary>
                {/* Chapter notes section */}
                <AccordionDetails
                  sx={{
                    pt: 0.75,
                    pb: 1.5,
                    px: 1.5,
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 2,
                    alignItems: 'flex-start',
                    // Stack thumbnail and text vertically on smaller screens
                    flexWrap: 'wrap',
                    [theme.breakpoints.down('sm')]: {
                      flexDirection: 'column',
                      alignItems: 'stretch',
                    },
                  }}
                >
                  {/* Thumbnail Section */}
                  {showScreenshots && (
                    <Box
                      sx={{
                        width: {
                          xs: '100%',
                          sm: '200px',
                        },
                        mb: {
                          xs: 1,
                          sm: 0,
                        },
                      }}
                    >
                      {/* Video thumbnail */}
                      <Box
                        onClick={() => handleTimestampClick(chapter.id)}
                        sx={{
                          width: '100%',
                          height: 112.5, // 16:9 aspect ratio for 200px width
                          backgroundColor: 'action.hover',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.02)',
                          },
                        }}
                      >
                        {/* Video thumbnail image */}
                        <Box
                          component="img"
                          src={youtubeImage}
                          alt={t('library.tour.chapters.thumbnail.alt')}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {highlightText(chapter.notes, searchTerm)}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
};

export default ChapterAccordionDemo;
